import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { NoAccessScreen } from './NoAccessScreen';
import { SignInScreen } from './SignInScreen';

/**
 * Gates the app behind an *invitation*, in two steps: signed in at all, then
 * known to us — an Eighty Days admin, or an active partner account.
 *
 * The second step is the one doing the work. Signing in is not a privilege we
 * grant: the Google provider accepts any Google account and the email/password
 * sign-up endpoint answers to the web API key that ships in our bundle, so
 * anybody who finds the URL can arrive here holding a valid session. Gating on
 * `user` alone would have handed all of them the catalogue — every experience,
 * every price, every tour leader.
 *
 * This is a courtesy, not the boundary. `isMember()` in firestore.rules and
 * storage.rules is what actually withholds the data, and it has to be, since a
 * stranger with a token need not run our JavaScript at all. What the screen
 * below adds is an explanation, in the one case where the refusal is a mistake:
 * a partner who picked the wrong account out of Google's chooser.
 *
 * The invitation route is mounted outside this gate — see App.tsx — since it
 * exists precisely for people who have no account yet.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { user, loading, isAdmin, isPartner } = useAuth();

  if (loading) {
    return (
      <div className='tw:flex tw:min-h-screen tw:items-center tw:justify-center tw:bg-ed-black'>
        <Loader2 className='tw:size-8 tw:animate-spin tw:text-ed-gold' />
      </div>
    );
  }

  if (!user) return <SignInScreen />;

  // `loading` above already waited for both role lookups, so this is a settled
  // answer rather than a flash of refusal on the way to the catalogue.
  if (!isAdmin && !isPartner) return <NoAccessScreen />;

  return <>{children}</>;
}
